import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Users, Star, Gift, TrendingUp, BarChart3, ArrowUp, ArrowDown, Minus } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useVenue } from "@/context/VenueContext";

const LVL_COLOR = { STEEL: "#9ca3af", BRONZE: "#c2703b", SILVER: "#c0c0c0", GOLD: "#d4af37", PLATINUM: "#67e8f9" };
const LVL_ORDER = ["PLATINUM", "GOLD", "SILVER", "BRONZE", "STEEL"];

function Trend({ cur, prev }) {
  const d = (cur || 0) - (prev || 0);
  const Icon = d === 0 ? Minus : d > 0 ? ArrowUp : ArrowDown;
  const col = d === 0 ? "text-zinc-500" : d > 0 ? "text-emerald-400" : "text-red-400";
  const label = prev > 0 ? `${d > 0 ? "+" : ""}${Math.round((d / prev) * 100)}%` : (d !== 0 ? `${d > 0 ? "+" : ""}${d}` : "—");
  return <span data-testid="dash-trend" className={`inline-flex items-center gap-0.5 text-[11px] font-medium ${col}`}><Icon className="w-3 h-3" />{label}</span>;
}

function Metric({ icon, label, value, color = "text-white" }) {
  return (
    <div className="rounded-xl bg-zinc-800/50 px-3 py-2.5 text-center">
      <div className={`flex items-center justify-center gap-1 font-display text-xl font-bold ${color}`}>{icon}{value}</div>
      <div className="text-[10px] text-zinc-500 mt-0.5">{label}</div>
    </div>
  );
}

export default function PlusDashboard() {
  const navigate = useNavigate();
  const { role, groups } = useVenue();
  const [days, setDays] = useState(30);
  const [groupId, setGroupId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!role) return;  // contesto non ancora pronto
    if (role === "PLATFORM_OWNER" && !groupId) return;  // attende selezione gruppo
    setLoading(true);
    try {
      const q = groupId ? `?days=${days}&group_id=${groupId}` : `?days=${days}`;
      const { data } = await api.get(`/plus/dashboard/venues${q}`);
      setData(data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [days, groupId, role]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (role === "PLATFORM_OWNER" && !groupId && groups.length) setGroupId(groups[0].id);
  }, [role, groups, groupId]);

  const maxVisite = Math.max(1, ...((data?.venues || []).map((r) => r.metrics.visite)));

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <button data-testid="dash-back" onClick={() => navigate("/")} className="p-2 rounded-lg border border-zinc-800 hover:border-zinc-600"><ArrowLeft className="w-4 h-4" /></button>
          <BarChart3 className="w-6 h-6 text-sky-400" />
          <h1 className="font-display text-2xl font-bold">Confronto Sale</h1>
          <div className="ml-auto flex items-center gap-2">
            {role === "PLATFORM_OWNER" && groups.length > 0 && (
              <select data-testid="dash-group" value={groupId || ""} onChange={(e) => setGroupId(e.target.value || null)} className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-1.5 text-sm outline-none">
                {groups.map((g) => <option key={g.id} value={g.id}>{g.nome}</option>)}
              </select>
            )}
            <div className="flex rounded-lg border border-zinc-800 overflow-hidden">
              {[7, 30, 90].map((d) => (
                <button key={d} data-testid={`dash-days-${d}`} onClick={() => setDays(d)} className={`px-3 py-1.5 text-sm ${days === d ? "bg-sky-500 text-white" : "text-zinc-400 hover:bg-zinc-900"}`}>{d}g</button>
              ))}
            </div>
          </div>
        </div>

        {loading && <div className="text-zinc-500 text-sm py-10 text-center">Caricamento…</div>}

        {!loading && data && (
          <>
            <div className="text-xs text-zinc-500 mb-3">{data.group_nome} · {data.venues.length} sale · ultimi {data.days} giorni</div>
            {/* Totali gruppo */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="dash-totals">
              <Metric icon={<Users className="w-4 h-4 mr-1 text-emerald-400" />} label="Presenti ora" value={data.totals.presenti_ora} color="text-emerald-300" />
              <Metric icon={<TrendingUp className="w-4 h-4 mr-1 text-sky-400" />} label="Visite" value={data.totals.visite} color="text-sky-300" />
              <Metric icon={<Star className="w-4 h-4 mr-1 text-amber-400 fill-amber-400" />} label="Stelle emesse" value={data.totals.stelle_emesse} color="text-amber-300" />
              <Metric icon={<Gift className="w-4 h-4 mr-1 text-fuchsia-400" />} label="Stelle riscattate" value={data.totals.stelle_riscattate} color="text-fuchsia-300" />
            </div>
            <div className="flex items-center gap-5 mb-8 text-xs -mt-4" data-testid="dash-totals-trend">
              <span className="text-zinc-500">Andamento gruppo vs periodo prec.</span>
              <span className="flex items-center gap-1 text-zinc-400">Visite <Trend cur={data.totals.visite} prev={data.totals.visite_prev} /></span>
              <span className="flex items-center gap-1 text-zinc-400">Stelle emesse <Trend cur={data.totals.stelle_emesse} prev={data.totals.stelle_emesse_prev} /></span>
            </div>

            {/* Barre confronto visite */}
            <div className="rounded-2xl bg-zinc-900 border border-zinc-800 p-5 mb-8" data-testid="dash-compare-visite">
              <div className="text-sm font-semibold mb-4">Visite per sala</div>
              <div className="space-y-3">
                {data.venues.map((r) => (
                  <div key={r.venue.id} className="flex items-center gap-3">
                    <span className="text-sm w-40 truncate">{r.venue.nome}</span>
                    <div className="flex-1 h-3 rounded-full bg-zinc-800 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-sky-500 to-sky-400" style={{ width: `${(r.metrics.visite / maxVisite) * 100}%` }} />
                    </div>
                    <span className="text-sm font-semibold w-12 text-right text-sky-300">{r.metrics.visite}</span>
                    <Trend cur={r.metrics.visite} prev={r.metrics.visite_prev} />
                  </div>
                ))}
              </div>
            </div>

            {/* Card per sala */}
            <div className="grid md:grid-cols-2 gap-4" data-testid="dash-venues">
              {data.venues.map((r) => {
                const dist = r.metrics.cv_distribuzione || {};
                const totDist = Object.values(dist).reduce((a, b) => a + b, 0) || 1;
                return (
                  <div key={r.venue.id} data-testid={`dash-venue-${r.venue.id}`} className="rounded-2xl bg-zinc-900 border border-zinc-800 p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <span className={`w-2 h-2 rounded-full ${r.venue.stato === "ACTIVE" ? "bg-emerald-400" : "bg-red-500"}`} />
                      <h3 className="font-display text-lg font-bold">{r.venue.nome}</h3>
                      <span className="ml-auto flex items-center gap-1 text-sm text-zinc-400"><Users className="w-3.5 h-3.5 text-emerald-400" /> {r.metrics.presenti_ora} ora</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      <Metric label="Visite" value={r.metrics.visite} color="text-sky-300" />
                      <Metric label="Clienti" value={r.metrics.clienti_unici} color="text-white" />
                      <Metric label="CV medio" value={r.metrics.cv_medio} color="text-sky-300" />
                      <Metric icon={<Star className="w-3.5 h-3.5 mr-1 text-amber-400 fill-amber-400" />} label="Emesse" value={r.metrics.stelle_emesse} color="text-amber-300" />
                      <Metric icon={<Gift className="w-3.5 h-3.5 mr-1 text-fuchsia-400" />} label="Riscattate" value={r.metrics.stelle_riscattate} color="text-fuchsia-300" />
                      <Metric label="Clienti CV" value={r.metrics.cv_conteggio} color="text-white" />
                    </div>
                    <div className="flex items-center gap-4 mb-4 text-xs" data-testid={`dash-trend-row-${r.venue.id}`}>
                      <span className="text-zinc-500">vs periodo prec.</span>
                      <span className="flex items-center gap-1 text-zinc-400">Visite <Trend cur={r.metrics.visite} prev={r.metrics.visite_prev} /></span>
                      <span className="flex items-center gap-1 text-zinc-400">Stelle <Trend cur={r.metrics.stelle_emesse} prev={r.metrics.stelle_emesse_prev} /></span>
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Reputazione</div>
                    <div className="flex h-3 rounded-full overflow-hidden mb-2 bg-zinc-800">
                      {LVL_ORDER.map((lv) => dist[lv] ? (
                        <div key={lv} title={`${lv}: ${dist[lv]}`} style={{ width: `${(dist[lv] / totDist) * 100}%`, background: LVL_COLOR[lv] }} />
                      ) : null)}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1">
                      {LVL_ORDER.map((lv) => dist[lv] ? (
                        <span key={lv} className="text-[11px]" style={{ color: LVL_COLOR[lv] }}>● {lv} {dist[lv]}</span>
                      ) : null)}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
